import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ChecklistOutlined,
  PlayArrowOutlined,
} from '@mui/icons-material';
import {
  reportsApi,
  type ClaimReadinessControlSummary,
  type ClaimReadinessTaskControlState,
  type DecisionReadinessPayload,
  type EffectivenessValidationWorkflow,
  type ValidationTask,
  type WorkflowClaimReadinessResponse,
} from '../services/reportsApi';
import {
  mergeLiveValidationControlState,
  type LiveValidationContext,
} from '../domain/incidents/groupedValidationTaskSync';

const TASK_STATUS_STYLES: Record<string, { label: string; color: string; bgcolor: string; borderColor: string }> = {
  open: { label: 'Open', color: '#B45309', bgcolor: '#FEF3C7', borderColor: '#FCD34D' },
  in_progress: { label: 'In progress', color: '#1D4ED8', bgcolor: '#DBEAFE', borderColor: '#93C5FD' },
  completed: { label: 'Completed', color: '#166534', bgcolor: '#DCFCE7', borderColor: '#86EFAC' },
  waived: { label: 'Waived', color: '#475569', bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
  cancelled: { label: 'Cancelled', color: '#475569', bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
};

const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

interface PendingTaskAction {
  taskId: string;
  blockerType: string;
  mode: 'complete' | 'waive';
  note: string;
  resultPayload: Record<string, any>;
}

type ValidationWorkflowExtraBlocker = {
  blocker_type?: string | null;
  source_label?: string | null;
  details?: string | null;
};

function canonicalControlForBlocker(
  controls: ClaimReadinessControlSummary[],
  blockerType: string,
): ClaimReadinessControlSummary | null {
  const key = String(blockerType || '').trim().toLowerCase();
  return controls.find((control) => String(control.control_id || '').trim().toLowerCase() === key) || null;
}

function isCanonicalControlResolved(
  control: ClaimReadinessControlSummary | null,
  controlState?: ClaimReadinessTaskControlState | null,
): boolean {
  const status = String(control?.status || '').trim().toLowerCase();
  if (status === 'resolved' || status === 'not_applicable') return true;
  if (controlState?.resolved === true) return true;
  return false;
}

function mergedControlPayload(controlState?: ClaimReadinessTaskControlState | null): Record<string, any> {
  return {
    ...(controlState?.control_payload || {}),
    ...(controlState?.result_payload || {}),
  };
}

function isBlockingCanonicalControl(control: ClaimReadinessControlSummary): boolean {
  if (control.blocking === false) return false;
  const status = String(control.status || '').trim().toLowerCase();
  return Boolean(status && status !== 'resolved' && status !== 'not_applicable');
}

function buildExtraBlockersFromCanonicalControls(
  controls: ClaimReadinessControlSummary[],
): ValidationWorkflowExtraBlocker[] {
  return controls
    .filter(isBlockingCanonicalControl)
    .map((control) => {
      const blockerType = String(control.control_id || '').trim().toLowerCase() || 'other';
      const sourceLabel = String(control.label || control.source_label || control.control_id || '').trim();
      const details = String(control.next_action?.label || control.auto_resolution_reason || '').trim() || null;
      return {
        blocker_type: blockerType,
        source_label: sourceLabel || blockerType,
        details,
      };
    })
    .filter((blocker) => Boolean(String(blocker.source_label || '').trim()));
}

function mergeExtraBlockers(
  supplied: ValidationWorkflowExtraBlocker[] | null | undefined,
  canonicalControls: ClaimReadinessControlSummary[],
): ValidationWorkflowExtraBlocker[] {
  const merged = [
    ...(Array.isArray(supplied) ? supplied : []),
    ...buildExtraBlockersFromCanonicalControls(canonicalControls),
  ];
  const seen = new Set<string>();
  return merged.filter((blocker) => {
    const blockerType = String(blocker.blocker_type || '').trim().toLowerCase() || 'other';
    const sourceLabel = String(blocker.source_label || '').trim();
    if (!sourceLabel) return false;
    const key = `${blockerType}:${sourceLabel.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function TaskStatusChip(props: { status?: string | null; blockerStatus?: string | null }) {
  const blockerKey = String(props.blockerStatus || '').toLowerCase();
  const key = String(props.status || 'open').toLowerCase();
  const style = blockerKey === 'resolved'
    ? { label: 'Resolved', color: '#166534', bgcolor: '#DCFCE7', borderColor: '#86EFAC' }
    : blockerKey === 'waived'
      ? TASK_STATUS_STYLES.waived
      : key === 'completed'
        ? { label: 'Recorded', color: '#92400E', bgcolor: '#FEF3C7', borderColor: '#FCD34D' }
        : TASK_STATUS_STYLES[key] || TASK_STATUS_STYLES.open;
  return (
    <Chip
      label={style.label}
      size="small"
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: '0.7rem',
        color: style.color,
        bgcolor: style.bgcolor,
        border: '1px solid',
        borderColor: style.borderColor,
        '& .MuiChip-label': { px: 0.95 },
      }}
    />
  );
}

function blockerPanelLabel(blockerType: string): string | null {
  if (blockerType === 'verification_method' || blockerType === 'field_confirmation_of_zone_controls') {
    return 'Open assign action';
  }
  if (blockerType === 'observation_coverage_reliability' || blockerType === 'matched_window_followup_validation') {
    return 'Open track impact';
  }
  return null;
}

function canManualComplete(blockerType: string): boolean {
  return blockerType !== 'verification_method' && blockerType !== 'matched_window_followup_validation';
}

function defaultResultPayload(
  task: ValidationTask,
  controlState?: ClaimReadinessTaskControlState | null,
): Record<string, any> {
  const blockerType = String(task.blocker_type || '').trim().toLowerCase();
  const payload = mergedControlPayload(controlState);
  if (blockerType === 'observation_coverage_reliability') {
    return {
      review_status: payload.review_status || '',
      reviewed: payload.reviewed === true,
      denominator_trust: payload.denominator_trust ?? null,
      baseline_observation_coverage_percent: payload.baseline_observation_coverage_percent ?? null,
      followup_observation_coverage_percent: payload.followup_observation_coverage_percent ?? null,
    };
  }
  if (blockerType === 'field_confirmation_of_zone_controls') {
    return {
      controls_present: payload.controls_present === true,
      controls_functioning: payload.controls_functioning === true,
    };
  }
  return {};
}

function buildAutoCompletePayload(
  blockerType: string,
  control: ClaimReadinessControlSummary | null,
  controlState?: ClaimReadinessTaskControlState | null,
): { note: string; resultPayload: Record<string, any>; buttonLabel: string } | null {
  const payload = mergedControlPayload(controlState);
  if (blockerType === 'verification_method' && payload.verification_method) {
    return {
      buttonLabel: 'Use saved method',
      note: `Verification method saved on the corrective action: ${payload.verification_method}.`,
      resultPayload: {
        verification_method: payload.verification_method,
      },
    };
  }
  if (
    blockerType === 'matched_window_followup_validation'
    && (
      payload.derived_from_live_measurement === true
      || (payload.baseline_window?.start && payload.followup_window?.start)
    )
  ) {
    return {
      buttonLabel: 'Use live measurement',
      note: control?.auto_resolution_reason || 'Matched baseline and follow-up windows are available in the live impact summary.',
      resultPayload: {
        derived_from_live_measurement: true,
        validation_method: 'Matched-window follow-up measurement from live workflow impact summary.',
        baseline_window: payload.baseline_window || {},
        followup_window: payload.followup_window || {},
      },
    };
  }
  return null;
}

function isPendingActionValid(pendingAction: PendingTaskAction | null): boolean {
  if (!pendingAction || !pendingAction.note.trim()) return false;
  if (pendingAction.mode !== 'complete') return true;
  if (pendingAction.blockerType === 'observation_coverage_reliability') {
    return pendingAction.resultPayload.reviewed === true
      || String(pendingAction.resultPayload.review_status || '').trim().toLowerCase() === 'reviewed';
  }
  if (pendingAction.blockerType === 'field_confirmation_of_zone_controls') {
    return pendingAction.resultPayload.controls_present === true
      && pendingAction.resultPayload.controls_functioning === true;
  }
  return true;
}

function isTaskResolved(task: ValidationTask & { blocker_status?: string | null }): boolean {
  const blockerStatus = String(task.blocker_status || '').toLowerCase();
  if (blockerStatus) {
    return blockerStatus === 'resolved' || blockerStatus === 'waived';
  }
  const taskStatus = String(task.status || '').toLowerCase();
  return taskStatus === 'completed' || taskStatus === 'waived' || taskStatus === 'cancelled';
}

function isTaskWaived(task: ValidationTask & { blocker_status?: string | null }): boolean {
  const blockerStatus = String(task.blocker_status || '').toLowerCase();
  const taskStatus = String(task.status || '').toLowerCase();
  return blockerStatus === 'waived' || taskStatus === 'waived';
}

function resolveTaskDisplayBlockerStatus(args: {
  canonicalResolved: boolean;
  task: ValidationTask & { blocker_status?: string | null };
}): string {
  if (args.canonicalResolved) return 'resolved';
  if (isTaskWaived(args.task)) return 'waived';
  const blockerStatus = String(args.task.blocker_status || '').toLowerCase();
  const taskStatus = String(args.task.status || '').toLowerCase();
  if (blockerStatus === 'resolved') return 'resolved';
  if (blockerStatus === 'open' || blockerStatus === 'in_progress') {
    return blockerStatus;
  }
  if (taskStatus === 'completed') return 'resolved';
  return blockerStatus || taskStatus || 'open';
}

const VALIDATION_TASK_BLOCKER_TYPES = new Set([
  'verification_method',
  'observation_coverage_reliability',
  'field_confirmation_of_zone_controls',
  'matched_window_followup_validation',
]);

function renderCanonicalHelper(
  control: ClaimReadinessControlSummary | null,
  controlState?: ClaimReadinessTaskControlState | null,
) {
  const blockerType = String(control?.control_id || '').trim().toLowerCase();
  const payload = mergedControlPayload(controlState);
  const modeHint = control?.attestation_required
    ? 'This blocker requires structured attestation, not a free-form note alone.'
    : control?.auto_resolution_reason
      ? control.auto_resolution_reason
      : blockerType === 'verification_method' || blockerType === 'matched_window_followup_validation'
        ? 'This blocker auto-resolves once canonical workflow state is available.'
        : null;

  if (blockerType === 'verification_method') {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
          {payload.verification_method
            ? `Saved on corrective action: ${payload.verification_method}`
            : control?.next_action?.label || 'Save the verification method in Assign action before closing this blocker.'}
        </Typography>
        {modeHint ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{modeHint}</Typography> : null}
      </>
    );
  }
  if (blockerType === 'observation_coverage_reliability') {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
          {`Canonical coverage snapshot: baseline ${payload.baseline_observation_coverage_percent ?? 'n/a'}% · follow-up ${payload.followup_observation_coverage_percent ?? 'n/a'}% · denominator trust ${payload.denominator_trust || 'n/a'}.`}
        </Typography>
        {modeHint ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{modeHint}</Typography> : null}
      </>
    );
  }
  if (blockerType === 'field_confirmation_of_zone_controls') {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
          {control?.next_action?.label || 'Record a structured field attestation instead of a free-form note so the next report can rely on it.'}
        </Typography>
        {modeHint ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{modeHint}</Typography> : null}
      </>
    );
  }
  if (blockerType === 'matched_window_followup_validation') {
    return (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
          {payload.derived_from_live_measurement || (payload.baseline_window?.start && payload.followup_window?.start)
            ? 'Canonical workflow state already has matched baseline and follow-up windows.'
            : control?.next_action?.label || 'The live impact summary still needs enough comparable follow-up data.'}
        </Typography>
        {modeHint ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{modeHint}</Typography> : null}
      </>
    );
  }
  return control?.next_action?.label ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
      {control.next_action.label}
    </Typography>
  ) : null;
}

function ValidationTaskRow(props: {
  task: ValidationTask;
  canonicalControl: ClaimReadinessControlSummary | null;
  controlState?: ClaimReadinessTaskControlState | null;
  pendingAction: PendingTaskAction | null;
  busy: boolean;
  onStartAction: (task: ValidationTask, mode: 'complete' | 'waive') => void;
  onNoteChange: (note: string) => void;
  onResultPayloadChange: (patch: Record<string, any>) => void;
  onConfirmAction: () => void;
  onCancelAction: () => void;
  onAutoComplete: (task: ValidationTask) => void;
  onFocusBlockerType?: (blockerType: string) => void;
  readOnly?: boolean;
}) {
  const task = props.task;
  const taskId = String(task.task_id || '');
  const blockerType = String(task.blocker_type || '').trim().toLowerCase();
  const status = String(task.status || 'open').toLowerCase();
  const canonicalResolved = isCanonicalControlResolved(props.canonicalControl, props.controlState);
  const blockerResolved = canonicalResolved || isTaskResolved(task as ValidationTask & { blocker_status?: string | null });
  const isOpen = status === 'open' || status === 'in_progress';
  const pending = props.pendingAction && props.pendingAction.taskId === taskId ? props.pendingAction : null;
  const autoComplete = buildAutoCompletePayload(blockerType, props.canonicalControl, props.controlState);
  const focusLabel = blockerPanelLabel(blockerType);
  const effectiveBlockerStatus = resolveTaskDisplayBlockerStatus({
    canonicalResolved,
    task: task as ValidationTask & { blocker_status?: string | null },
  });

  return (
    <Box
      sx={{
        px: 1.4,
        py: 1.1,
        borderRadius: 2,
        border: '1px solid #E2E8F0',
        bgcolor: status === 'completed' || status === 'waived' ? '#F8FAFC' : '#FFFFFF',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {String(task.title || props.canonicalControl?.label || 'Validation task')}
          </Typography>
          {task.description ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
              {task.description}
            </Typography>
          ) : null}
          {renderCanonicalHelper(props.canonicalControl, props.controlState)}
          {task.evidence_note && status === 'completed' ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#166534' }}>
              {`Evidence: ${task.evidence_note}`}
            </Typography>
          ) : null}
          {canonicalResolved && status !== 'completed' ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#166534' }}>
              {props.canonicalControl?.auto_resolution_reason
                || `Resolved from canonical ${props.canonicalControl?.label || 'control'} state.`}
            </Typography>
          ) : null}
          {status === 'completed' && !blockerResolved ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#B45309' }}>
              Structured resolution is still required. A completed note alone does not clear this blocker.
            </Typography>
          ) : null}
          {task.waiver_reason && (status === 'waived' || status === 'cancelled') ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
              {`Reason: ${task.waiver_reason}`}
            </Typography>
          ) : null}
          {!canonicalResolved && isTaskWaived(task as ValidationTask & { blocker_status?: string | null }) ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#B45309' }}>
              Waived for audit trail only — this control still counts toward claim readiness until structured sign-off is saved.
            </Typography>
          ) : null}
        </Box>
        <TaskStatusChip status={task.status} blockerStatus={effectiveBlockerStatus} />
      </Stack>
      {isOpen && !pending && !blockerResolved && !props.readOnly ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
          {autoComplete ? (
            <Button size="small" variant="outlined" disabled={props.busy} onClick={() => props.onAutoComplete(task)}>
              {autoComplete.buttonLabel}
            </Button>
          ) : null}
          {focusLabel && props.onFocusBlockerType ? (
            <Button
              size="small"
              color="inherit"
              disabled={props.busy}
              onClick={() => props.onFocusBlockerType?.(blockerType)}
            >
              {focusLabel}
            </Button>
          ) : null}
          {canManualComplete(blockerType) ? (
            <Button size="small" variant="outlined" disabled={props.busy} onClick={() => props.onStartAction(task, 'complete')}>
              Mark complete
            </Button>
          ) : null}
          <Button size="small" color="inherit" disabled={props.busy} onClick={() => props.onStartAction(task, 'waive')}>
            Waive
          </Button>
        </Stack>
      ) : null}
      {pending ? (
        <Box sx={{ mt: 1.1 }}>
          {pending.mode === 'complete' && pending.blockerType === 'observation_coverage_reliability' ? (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={pending.resultPayload.reviewed === true}
                  onChange={(event) => props.onResultPayloadChange({
                    reviewed: event.target.checked,
                    review_status: event.target.checked ? 'reviewed' : '',
                  })}
                />
              )}
              label="I reviewed the uptime / coverage evidence for these comparison windows"
            />
          ) : null}
          {pending.mode === 'complete' && pending.blockerType === 'field_confirmation_of_zone_controls' ? (
            <Stack spacing={0.2} sx={{ mb: 0.75 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={pending.resultPayload.controls_present === true}
                    onChange={(event) => props.onResultPayloadChange({ controls_present: event.target.checked })}
                  />
                )}
                label="Controls are present in the field"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={pending.resultPayload.controls_functioning === true}
                    onChange={(event) => props.onResultPayloadChange({ controls_functioning: event.target.checked })}
                  />
                )}
                label="Controls are functioning as intended"
              />
            </Stack>
          ) : null}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={pending.note}
            onChange={(event) => props.onNoteChange(event.target.value)}
            label={pending.mode === 'complete' ? 'Evidence note (required)' : 'Waiver reason (required)'}
            placeholder={
              pending.mode === 'complete'
                ? 'Describe what was verified and how.'
                : 'Explain why this validation step is being waived.'
            }
          />
          <Stack direction="row" spacing={1} sx={{ mt: 0.9 }}>
            <Button
              size="small"
              variant="contained"
              disabled={props.busy || !isPendingActionValid(pending)}
              onClick={props.onConfirmAction}
            >
              {pending.mode === 'complete' ? 'Confirm completion' : 'Confirm waiver'}
            </Button>
            <Button size="small" color="inherit" disabled={props.busy} onClick={props.onCancelAction}>
              Cancel
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

export interface ClaimReadinessValidationSectionProps {
  workflowId?: string | null;
  reportId?: string | null;
  safetyCaseId?: string | null;
  decisionReadiness?: DecisionReadinessPayload | null;
  missingInputCount?: number;
  extraBlockers?: Array<{
    blocker_type?: string | null;
    source_label?: string | null;
    details?: string | null;
  }>;
  refreshKey?: string | number;
  onFocusBlockerType?: (blockerType: string) => void;
  sectionTitle?: string;
  sectionSubtitle?: string;
  createButtonLabel?: string;
  startPromptText?: string;
  readOnly?: boolean;
  liveValidationContext?: LiveValidationContext | null;
}

export function ClaimReadinessValidationSection(props: ClaimReadinessValidationSectionProps) {
  const workflowId = String(props.workflowId || '').trim();
  const reportId = String(props.reportId || '').trim() || null;
  const sectionTitle = String(props.sectionTitle || 'Effectiveness validation workflow').trim();
  const sectionSubtitle = String(
    props.sectionSubtitle
    || 'Clears claim readiness blockers from the Signed Safety Scan as auditable validation tasks. The Signed Safety Scan itself stays unchanged; completed validation feeds the next, stronger Safety Scan.',
  ).trim();
  const createButtonLabel = String(props.createButtonLabel || 'Start validation workflow').trim();
  const startPromptText = String(props.startPromptText || '').trim();
  const refreshKey = props.refreshKey;
  const fetchScopeKey = `${workflowId}:${reportId || ''}:${String(refreshKey || '')}`;
  const fetchScopeRef = useRef(fetchScopeKey);
  fetchScopeRef.current = fetchScopeKey;

  const [loading, setLoading] = useState<boolean>(Boolean(workflowId));
  const [busy, setBusy] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [claimReadinessSummary, setClaimReadinessSummary] = useState<WorkflowClaimReadinessResponse | null>(null);
  const [validationWorkflow, setValidationWorkflow] = useState<EffectivenessValidationWorkflow | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingTaskAction | null>(null);

  const applyClaimReadinessResponse = useCallback((
    response: WorkflowClaimReadinessResponse | null | undefined,
    scopeKey: string,
  ) => {
    if (fetchScopeRef.current !== scopeKey) return;
    if (response?.ok) {
      setClaimReadinessSummary(response);
      const workflows = Array.isArray(response.validation_workflows) ? response.validation_workflows : [];
      setValidationWorkflow(workflows.length ? workflows[0] : null);
    } else {
      setClaimReadinessSummary(null);
      setValidationWorkflow(null);
    }
    setErrorText('');
  }, []);

  const refresh = useCallback(async () => {
    const scopeKey = fetchScopeRef.current;
    if (!workflowId) {
      setLoading(false);
      setClaimReadinessSummary(null);
      setValidationWorkflow(null);
      return;
    }
    setLoading(true);
    setClaimReadinessSummary(null);
    setValidationWorkflow(null);
    try {
      const response = await reportsApi.getWorkflowClaimReadiness(workflowId, reportId);
      if (fetchScopeRef.current !== scopeKey) return;
      applyClaimReadinessResponse(response, scopeKey);
    } catch {
      if (fetchScopeRef.current !== scopeKey) return;
      setClaimReadinessSummary(null);
      setValidationWorkflow(null);
      setErrorText('Validation workflow status could not be loaded.');
    } finally {
      if (fetchScopeRef.current === scopeKey) setLoading(false);
    }
  }, [applyClaimReadinessResponse, reportId, workflowId]);

  useEffect(() => {
    setClaimReadinessSummary(null);
    setValidationWorkflow(null);
    setErrorText('');
    if (!workflowId) {
      setLoading(false);
      return undefined;
    }
    const scopeKey = fetchScopeKey;
    setLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const response = await reportsApi.getWorkflowClaimReadiness(workflowId, reportId);
        if (cancelled || fetchScopeRef.current !== scopeKey) return;
        applyClaimReadinessResponse(response, scopeKey);
      } catch {
        if (cancelled || fetchScopeRef.current !== scopeKey) return;
        setClaimReadinessSummary(null);
        setValidationWorkflow(null);
        setErrorText('Validation workflow status could not be loaded.');
      } finally {
        if (!cancelled && fetchScopeRef.current === scopeKey) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyClaimReadinessResponse, fetchScopeKey, reportId, workflowId]);

  const canonicalControls = useMemo<ClaimReadinessControlSummary[]>(
    () => {
      const controls = claimReadinessSummary?.controls;
      return Array.isArray(controls) ? controls : [];
    },
    [claimReadinessSummary?.controls],
  );
  const controlsByType = useMemo(
    () => {
      const byType = claimReadinessSummary?.controls_by_type;
      return byType && typeof byType === 'object' ? byType : {};
    },
    [claimReadinessSummary?.controls_by_type],
  );
  const resolveTaskControlState = useCallback((blockerType: string) => (
    mergeLiveValidationControlState(
      blockerType,
      controlsByType[blockerType],
      props.liveValidationContext,
    )
  ), [controlsByType, props.liveValidationContext]);
  const createWorkflowExtraBlockers = useMemo(
    () => mergeExtraBlockers(
      Array.isArray(props.extraBlockers) ? props.extraBlockers : [],
      canonicalControls,
    ),
    [canonicalControls, props.extraBlockers],
  );
  const hasCanonicalControls = canonicalControls.length > 0;
  const unresolvedBlockingCount = useMemo(() => {
    if (!hasCanonicalControls) return 0;
    return canonicalControls.filter(isBlockingCanonicalControl).length;
  }, [canonicalControls, hasCanonicalControls]);
  const effectiveMissingInputCount = hasCanonicalControls
    ? unresolvedBlockingCount
    : Number(props.missingInputCount || 0);

  const handleCreate = useCallback(async () => {
    if (!workflowId) return;
    setBusy(true);
    setErrorText('');
    try {
      const response = await reportsApi.createValidationWorkflow(workflowId, {
        report_id: reportId,
        safety_case_id: props.safetyCaseId || null,
        decision_readiness: props.decisionReadiness || null,
        extra_blockers: createWorkflowExtraBlockers,
      });
      if (response.validation_workflow) {
        setValidationWorkflow(response.validation_workflow);
        await refresh();
      } else {
        setErrorText(response.message || 'Validation workflow could not be created.');
      }
    } catch (err: any) {
      setErrorText(
        String(err?.response?.data?.message || '') || 'Validation workflow could not be created.',
      );
    } finally {
      setBusy(false);
    }
  }, [createWorkflowExtraBlockers, props.decisionReadiness, props.safetyCaseId, refresh, reportId, workflowId]);

  const patchTask = useCallback(async (
    taskId: string,
    input: {
      status: 'completed' | 'waived';
      evidence_note?: string;
      waiver_reason?: string;
      result_payload?: Record<string, any>;
    },
  ) => {
    setBusy(true);
    setErrorText('');
    try {
      const response = await reportsApi.patchValidationTask(taskId, input);
      if (response.validation_workflow) {
        setValidationWorkflow(response.validation_workflow);
      }
      setPendingAction(null);
      await refresh();
    } catch (err: any) {
      setErrorText(
        String(err?.response?.data?.message || '') || 'Validation task could not be updated.',
      );
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const handleConfirmTaskAction = useCallback(async () => {
    if (!pendingAction || !isPendingActionValid(pendingAction)) return;
    await patchTask(pendingAction.taskId, pendingAction.mode === 'complete'
      ? {
        status: 'completed',
        evidence_note: pendingAction.note.trim(),
        result_payload: pendingAction.resultPayload,
      }
      : {
        status: 'waived',
        waiver_reason: pendingAction.note.trim(),
      });
  }, [patchTask, pendingAction]);

  const rawTasks = useMemo(
    () => (Array.isArray(validationWorkflow?.tasks) ? validationWorkflow?.tasks || [] : []),
    [validationWorkflow],
  );
  const blockerById = useMemo(() => new Map(
    (validationWorkflow?.blockers || []).map((blocker) => [String(blocker.blocker_id || ''), blocker]),
  ), [validationWorkflow]);
  const tasks = useMemo(
    () => rawTasks.map((task) => {
      const blocker = blockerById.get(String(task.blocker_id || ''));
      return {
        ...task,
        blocker_type: task.blocker_type || blocker?.blocker_type || null,
        source_label: task.source_label || blocker?.source_label || null,
        blocker_status: blocker?.status || null,
      };
    }),
    [blockerById, rawTasks],
  );
  const progress = validationWorkflow?.progress || null;
  const totalTasks = Number(progress?.total_tasks || tasks.length || 0);
  const resolvedTasks = tasks.filter((task) => {
    const blockerType = String(task.blocker_type || '').trim().toLowerCase();
    const canonicalControl = canonicalControlForBlocker(canonicalControls, blockerType);
    const controlState = resolveTaskControlState(blockerType);
    return isCanonicalControlResolved(canonicalControl, controlState);
  }).length;
  const workflowStatusKey = String(validationWorkflow?.status || '').toLowerCase();
  const claimResolvedCount = Number(claimReadinessSummary?.resolved_count || 0);
  const claimTotalCount = Number(
    claimReadinessSummary?.total_count
    || canonicalControls.length
    || 0,
  );
  const claimReadinessComplete = claimTotalCount > 0 && claimResolvedCount >= claimTotalCount;
  const unresolvedClaimControls = useMemo(
    () => canonicalControls.filter(isBlockingCanonicalControl),
    [canonicalControls],
  );
  const unresolvedNonTaskControls = useMemo(
    () => unresolvedClaimControls.filter(
      (control) => !VALIDATION_TASK_BLOCKER_TYPES.has(String(control.control_id || '').trim().toLowerCase()),
    ),
    [unresolvedClaimControls],
  );
  const workflowStatusLabel = workflowStatusKey === 'completed' && !claimReadinessComplete
    ? 'Tasks closed'
    : (WORKFLOW_STATUS_LABELS[workflowStatusKey] || 'Open');
  const workflowStatusColor = workflowStatusKey === 'completed'
    ? (claimReadinessComplete ? 'success' : 'warning')
    : 'default';
  const claimReadinessSummaryLabel = useMemo(() => {
    const totalCount = Number(claimReadinessSummary?.total_count || canonicalControls.length || 0);
    if (!totalCount) return '';
    const resolvedCount = Number(claimReadinessSummary?.resolved_count || 0);
    return `Claim readiness: ${resolvedCount}/${totalCount} resolved`;
  }, [canonicalControls.length, claimReadinessSummary?.resolved_count, claimReadinessSummary?.total_count]);

  if (!workflowId) return null;
  if (!validationWorkflow && !effectiveMissingInputCount && !loading) return null;

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }} data-testid="claim-readiness-validation-section">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <ChecklistOutlined aria-hidden sx={{ fontSize: 20, color: '#7C3AED' }} />
              <Typography component="h2" variant="h6">
                {sectionTitle}
              </Typography>
              {claimReadinessSummaryLabel ? (
                <Chip size="small" variant="outlined" label={claimReadinessSummaryLabel} />
              ) : null}
              {validationWorkflow ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={workflowStatusLabel}
                  color={workflowStatusColor}
                />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sectionSubtitle}
            </Typography>
          </Box>
        </Stack>

        {errorText ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {errorText}
          </Alert>
        ) : null}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Checking validation workflow status...
            </Typography>
          </Stack>
        ) : validationWorkflow ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.2 }}>
              {totalTasks
                ? `${resolvedTasks} of ${totalTasks} validation task${totalTasks === 1 ? '' : 's'} cleared for sign-off`
                : 'No validation tasks recorded yet.'}
            </Typography>
            {!claimReadinessComplete && unresolvedClaimControls.length ? (
              <Alert severity="info" sx={{ mb: 1.2 }}>
                {unresolvedNonTaskControls.length
                  ? `${unresolvedClaimControls.length} claim-readiness control${unresolvedClaimControls.length === 1 ? '' : 's'} still open (${unresolvedNonTaskControls.map((control) => String(control.label || control.control_id || 'control')).join(', ')} not tracked as validation tasks). Waiving a validation task does not always clear sign-off — field zone confirmation needs a structured attestation in Assign action.`
                  : `${unresolvedClaimControls.length} validation task${unresolvedClaimControls.length === 1 ? '' : 's'} still need structured sign-off before claim readiness is complete. Waiving records the decision but does not clear field confirmation or coverage blockers.`}
              </Alert>
            ) : null}
            <Stack spacing={1}>
              {tasks.map((task) => {
                const blockerType = String(task.blocker_type || '').trim().toLowerCase();
                const taskControlState = resolveTaskControlState(blockerType);
                return (
                  <ValidationTaskRow
                    key={String(task.task_id || task.title)}
                    task={task}
                    canonicalControl={canonicalControlForBlocker(canonicalControls, blockerType)}
                    controlState={taskControlState}
                    pendingAction={pendingAction}
                    busy={busy}
                    onStartAction={(selectedTask, mode) => setPendingAction({
                      taskId: String(selectedTask.task_id || ''),
                      blockerType: String(selectedTask.blocker_type || '').trim().toLowerCase(),
                      mode,
                      note: '',
                      resultPayload: defaultResultPayload(
                        selectedTask,
                        resolveTaskControlState(String(selectedTask.blocker_type || '').trim().toLowerCase()),
                      ),
                    })}
                    onNoteChange={(note) => setPendingAction((prev) => (prev ? { ...prev, note } : prev))}
                    onResultPayloadChange={(patch) => setPendingAction((prev) => (
                      prev
                        ? { ...prev, resultPayload: { ...(prev.resultPayload || {}), ...patch } }
                        : prev
                    ))}
                    onConfirmAction={() => void handleConfirmTaskAction()}
                    onCancelAction={() => setPendingAction(null)}
                    onAutoComplete={(selectedTask) => {
                      const type = String(selectedTask.blocker_type || '').trim().toLowerCase();
                      const autoComplete = buildAutoCompletePayload(
                        type,
                        canonicalControlForBlocker(canonicalControls, type),
                        resolveTaskControlState(type),
                      );
                      if (!autoComplete) return;
                      void patchTask(String(selectedTask.task_id || ''), {
                        status: 'completed',
                        evidence_note: autoComplete.note,
                        result_payload: autoComplete.resultPayload,
                      });
                    }}
                    onFocusBlockerType={props.onFocusBlockerType}
                    readOnly={props.readOnly}
                  />
                );
              })}
            </Stack>
          </>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.2 }}>
              {startPromptText || (effectiveMissingInputCount
                ? `${effectiveMissingInputCount} blocking control${effectiveMissingInputCount === 1 ? '' : 's'} can be turned into actionable validation tasks.`
                : 'No claim readiness blockers were identified for this workflow.')}
            </Typography>
            {effectiveMissingInputCount && !props.readOnly ? (
              <Button
                variant="contained"
                size="small"
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PlayArrowOutlined />}
                disabled={busy}
                onClick={() => void handleCreate()}
              >
                {createButtonLabel}
              </Button>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
